import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, MapPin, Building, DollarSign } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const departments = ["All", "Engineering", "Product", "Design", "Data", "Sales", "Growth", "Customer Success", "Marketing"];
const seniorities = ["All", "IC", "Mid", "Senior", "Staff", "Manager", "Director", "VP", "C-level"];
const locationTypes = ["All", "Remote", "Hybrid", "On-site"];

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ department: "All", seniority: "All", locationType: "All", search: "" });

  useEffect(() => {
    axios.get(`${API}/jobs`)
      .then(res => setJobs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredJobs = jobs.filter(job => {
    if (filters.department !== "All" && job.department !== filters.department) return false;
    if (filters.seniority !== "All" && job.seniority !== filters.seniority) return false;
    if (filters.locationType !== "All" && job.location_type !== filters.locationType) return false;
    if (filters.search && !job.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const currencySymbols = { USD: "$", GBP: "£", AED: "AED " };
  const formatSalary = (min, max, currency = "USD") => {
    if (!min && !max) return null;
    const sym = currencySymbols[currency] || "$";
    const fmt = n => `${sym}${(n/1000).toFixed(0)}k`;
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    return min ? `${fmt(min)}+` : `Up to ${fmt(max)}`;
  };

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title="Jobs - Open Roles at VC-Backed Startups"
        description="Browse curated positions at exceptional VC-backed SaaS companies. Engineering, Product, Growth, Sales, and Customer Success roles from IC to leadership. Remote, hybrid, and on-site."
        keywords="startup jobs board, SaaS job openings, engineering jobs VC startups, product manager openings, growth marketing positions, tech careers startups, remote SaaS jobs, senior engineer jobs, VP engineering openings, startup CTO jobs"
        canonical="https://magnuskor.com/jobs"
      />
      {/* Header */}
      <section className="py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-overline">Opportunities</span>
          <h1 className="heading-hero mt-4 mb-6">Open roles</h1>
          <p className="text-body-lg max-w-xl">
            Curated positions at exceptional VC-backed companies—from IC roles to leadership.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-gray-100 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-grow max-w-xs">
              <Search size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search roles..."
                value={filters.search}
                onChange={e => setFilters({...filters, search: e.target.value})}
                className="input-luxury pl-8"
                data-testid="job-search"
              />
            </div>
            <select
              value={filters.department}
              onChange={e => setFilters({...filters, department: e.target.value})}
              className="input-luxury max-w-[160px] cursor-pointer"
              data-testid="filter-department"
            >
              {departments.map(d => <option key={d} value={d}>{d === "All" ? "Department" : d}</option>)}
            </select>
            <select
              value={filters.seniority}
              onChange={e => setFilters({...filters, seniority: e.target.value})}
              className="input-luxury max-w-[140px] cursor-pointer"
              data-testid="filter-seniority"
            >
              {seniorities.map(s => <option key={s} value={s}>{s === "All" ? "Level" : s}</option>)}
            </select>
            <select
              value={filters.locationType}
              onChange={e => setFilters({...filters, locationType: e.target.value})}
              className="input-luxury max-w-[140px] cursor-pointer"
              data-testid="filter-location"
            >
              {locationTypes.map(l => <option key={l} value={l}>{l === "All" ? "Location" : l}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <p className="text-sm text-gray-500 mb-8">
            {filteredJobs.length} {filteredJobs.length === 1 ? "role" : "roles"} available
          </p>

          {loading ? (
            <div className="text-center py-20 text-gray-400">Loading...</div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 mb-4">No roles match your criteria.</p>
              <button onClick={() => setFilters({department:"All",seniority:"All",locationType:"All",search:""})} className="text-[#B8860B]">
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map(job => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="group block card-luxury p-8"
                  data-testid={`job-card-${job.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex-grow">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h2 className="font-display text-2xl group-hover:text-[#B8860B] transition-colors">
                          {job.title}
                        </h2>
                        {job.is_confidential && (
                          <span className="text-[10px] tracking-wider text-gray-400 border border-gray-200 px-2 py-0.5 uppercase">
                            Confidential
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-4">
                        Our client <span className="text-gray-400">· confidential (via Magnus Kor)</span>
                      </p>
                      {job.highlights?.length > 0 && (
                        <ul className="mb-4 space-y-1" data-testid={`job-highlights-${job.id}`}>
                          {job.highlights.slice(0,2).map((h, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="w-1 h-1 bg-[#B8860B] rounded-full mt-2 flex-shrink-0" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                        <span className="flex items-center gap-2">
                          <MapPin size={14} />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-2">
                          <Building size={14} />
                          {job.location_type}
                        </span>
                        {formatSalary(job.salary_min, job.salary_max, job.salary_currency) && (
                          <span className="flex items-center gap-2">
                            <DollarSign size={14} />
                            {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden md:block">
                        <span className="text-[#B8860B] text-xs tracking-wider uppercase">{job.department}</span>
                        <p className="text-gray-400 text-xs mt-1">{job.seniority}</p>
                      </div>
                      <ArrowRight size={20} className="text-gray-300 group-hover:text-[#B8860B] group-hover:translate-x-2 transition-all" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-8 text-center">
          <h2 className="heading-card mb-4">Don't see the right fit?</h2>
          <p className="text-body mb-8">
            Join our network and we'll reach out when matching opportunities arise.
          </p>
          <Link to="/for-talent" className="btn-luxury btn-luxury-outline">
            Join Our Network
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JobsPage;
